import React, { useCallback } from 'react';
import { Service } from '../../types/Service';
import { cloudinaryDeliveryUrl, photographAlt } from '../../utils/imageSeo';
import './ServiceCard.css';

interface Props {
  service: Service;
  onClick?: (service: Service) => void;
  delay?: number;
}

const ServiceCard: React.FC<Props> = ({ service, onClick, delay = 0 }) => {
  const getServiceSlug = useCallback(() => {
    if (service.slug) return service.slug;
    return service.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }, [service]);

  const price = service.isSpecial && service.specialPrice ? service.specialPrice : service.price;
  const hasDiscount = Boolean(service.isSpecial && service.specialPrice && service.price && service.specialPrice < service.price);
  const imageUrl = service.image && service.image.includes('res.cloudinary.com')
    ? cloudinaryDeliveryUrl(service.image, 800)
    : service.image;

  const formatDuration = (minutes: number): string => {
    const hours = minutes / 60;

    if (hours === 0.5) return '½ hour';
    if (hours === 0.25) return '¼ hour';
    if (hours === 0.75) return '¾ hour';
    if (hours === 1.5) return '1 ½ hours';
    if (hours === 2.5) return '2 ½ hours';
    if (hours === 3.5) return '3 ½ hours';
    if (hours === 4.5) return '4 ½ hours';
    if (hours === 5.5) return '5 ½ hours';
    if (hours === 6.5) return '6 ½ hours';
    if (hours === 7.5) return '7 ½ hours';
    if (hours === 8.5) return '8 ½ hours';

    if (hours % 1 === 0) {
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    }

    return `${hours} hours`;
  };

  return (
    <a
      href={`/services/${getServiceSlug()}`}
      className={`service-card ${service.isSpecial ? 'special-branding' : ''} ${service.badge ? `badge-${service.badge.toLowerCase()}` : ''}`}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
        if (!onClick) return;
        event.preventDefault();
        onClick(service);
      }}
      style={{ animationDelay: `${delay}s` }}
    >
      {imageUrl && (
        <div className="service-image">
          <img
            src={imageUrl}
            alt={photographAlt(service.name, { alt: service.name }, { hero: true, description: service.short })}
            loading="lazy"
            decoding="async"
          />
          <div className="service-overlay">
            <i className="fas fa-eye"></i>
          </div>
        </div>
      )}

      <div className="service-content">
        <div className="service-header">
          <h3 className="service-title">{service.name}</h3>
          {service.durationMinutes && (
            <div className="service-duration">
              <i className="fas fa-clock"></i>
              <span>{formatDuration(service.durationMinutes)}</span>
            </div>
          )}
        </div>

        {service.short && (
          <p className="service-short">{service.short}</p>
        )}

        {service.features && service.features.length > 0 && (
          <ul className="service-features">
            {service.features.slice(0, 3).map((feature, index) => (
              <li key={index}>
                <i className="fas fa-check"></i>
                <span>{feature}</span>
              </li>
            ))}
            {service.features.length > 3 && (
              <li className="more-features">
                <span>+{service.features.length - 3} more</span>
              </li>
            )}
          </ul>
        )}

        {price != null && (
          <div className="service-pricing">
            {hasDiscount && (
              <span className="original-price">
                {service.currency || 'R'} {service.price?.toFixed(0)}
              </span>
            )}
            <span className="current-price">
              {service.currency || 'R'} {price?.toFixed(0)}
              {service.isSpecial && <span className="special-label">Special</span>}
            </span>
          </div>
        )}

        {(service.category || service.badge) && (
          <div className="service-badges">
            {service.category && <span className="pill-badge">{service.category}</span>}
            {service.badge && <span className="pill-badge alt">{service.badge}</span>}
          </div>
        )}

        <div className="service-ctas">
          <span className="btn btn-primary">
            <span>View Details</span>
            <i className="fas fa-arrow-right"></i>
          </span>
        </div>
      </div>
    </a>
  );
};

export default ServiceCard;
